import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import FinalRosterExportContext from "../../contexts/FinalRosterExportContext";
import logo from "../../assets/logo.png";
import logoWithQual from "../../assets/logo-with-qual.png";
import logoWithMain from "../../assets/WBC_Main.png";
import { formatDate } from "../../utils/DateUtils";
import { MAIN, QUALIFIERS, QUALIFYING } from "../../constants/TournamentTypeConstants";
import { properNoun } from "../../utils/StringUtils";
import PositionConstants from "../../constants/PositionConstants";

const Pages = styled.div`
  position: absolute;
  margin: 0;
  background-color: white;
  font-family: Roboto, Arial, sans-serif;
  @media print {
    position: static; /* let normal flow determine placement on printed page */
  }
  .wbc-capture & {
    position: static;
  }
`;

const Page = styled.div`
  height: 8.25in;
  width: 10.5in;
  page-break-after: always;
  display: flex;
  flex-direction: column;
  background: white;
  @media print {
    height: auto; /* allow content (e.g., Numerical Guide) to flow onto page 2 */
  }
`;

const LoadingPage = styled(Page)`
  align-items: center;
  justify-content: center;
  #loading-wrapper {
    display: flex;
    font-size: 24px;
    img {
      -webkit-animation: spin 4s linear infinite;
      -moz-animation: spin 4s linear infinite;
      animation: spin 4s linear infinite;
    }
    @-moz-keyframes spin {
      100% {
        -moz-transform: rotate(360deg);
      }
    }
    @-webkit-keyframes spin {
      100% {
        -webkit-transform: rotate(360deg);
      }
    }
    @keyframes spin {
      100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
      }
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  text-transform: uppercase;
  color: navy;
  z-index: 1055; // place in front of navbar
`;
const TeamStaffBanner = styled.div`
  top: 0;
  left: 0;
  width: 100%;
  height: 18px;
  background-color: navy;
  border: 1px solid black;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  padding: 2px 4px;
  margin-bottom: 0.5rem;
  z-index: 1000;
  flex-wrap: wrap;

  @media print {
    margin-bottom: 0.3rem; /* reduce margin in print */
  }

  .staff-position {
    font-weight: bold;
  }

  .staff-name {
    font-weight: normal;
    margin-right: 15px;
  }
`;

const Body = styled.div`
  display: flex;
  height: 100%;
  z-index: 1056; // place in front of wbc-footer
  table {
    margin-bottom: 0.5rem;
    @media print {
      margin-bottom: 0.25rem; /* tighter vertical spacing on print */
    }
    thead {
      border-bottom: 1px solid black;
      @media print {
        display: table-header-group;
        break-after: avoid;
        page-break-after: avoid;
        th {
          position: static !important;
        }
      }
    }
    tbody {
      @media print {
        break-before: avoid;
        page-break-before: avoid;
        tr:first-child td {
          padding-top: 8px; /* ensure space below repeated header */
        }
      }
    }
    tr {
      line-height: 0.6rem;
      font-size: 0.6rem;
      @media print {
        page-break-inside: avoid;
        break-inside: avoid;
        line-height: 0.55rem; /* tighter row height in print */
      }
    }
    td {
      padding: 0.125rem;
      @media print {
        padding: 0.1rem 0.125rem; /* reduce vertical padding in print */
      }
      &.td-bold {
        font-weight: bold;
      }
    }
    @media print {
      width: 100%;
      border-collapse: separate; /* avoid header/body overlay in some engines */
      border-spacing: 0;
    }
  }
  @media print {
    display: table; /* stable two-column layout for print */
    table-layout: fixed;
    height: auto;
    overflow: visible;
    /* clearfix so floats affect container height */
    &::after {
      content: "";
      display: none;
    }
  }
  .wbc-capture & {
    display: table;
    table-layout: fixed;
    height: auto;
    overflow: visible;
  }
  .wbc-capture & table {
    width: 100%;
    border-collapse: separate; /* avoid header/body overlay in some engines */
    border-spacing: 0;
  }
  .wbc-capture & thead {
    display: table-header-group;
  }
  .wbc-capture & tbody tr:first-child td {
    padding-top: 8px; /* ensure space below repeated header in capture */
  }
  .wbc-capture & table {
    margin-bottom: 0.25rem; /* tighter vertical spacing in capture */
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 76%;
  height: 100%;
  top: -24px;
  padding-left: 0.75rem;
  @media print {
    height: auto;
    top: 0;
    display: table-cell;
    width: 76%;
    vertical-align: top;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  row-gap: 0.25rem;
  width: 24%;
  height: 90%;
  padding-right: 0.125rem;
  overflow: hidden;
  @media print {
    height: auto;
    overflow: visible;
    display: table-cell;
    width: 24%;
    vertical-align: top;
    justify-content: flex-start; /* avoid large gaps pushing content to bottom */
    align-items: stretch;
    row-gap: 0.2rem;
  }
`;

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StaffTableContainer = styled(TableContainer)`
  @media print {
    break-before: auto;
    page-break-before: auto;
  }
`;

const NumericalGuide = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: 0.5rem;
  padding: 0.5rem;
  border: 1px solid black;
  @media print {
    page-break-inside: auto; /* allow this box to split across pages */
    break-inside: auto;
    margin: 0.15rem 0.2rem; /* reduce margins to fit more content */
    padding: 0.3rem;
  }
  .wbc-capture & {
    page-break-inside: auto;
    break-inside: auto;
    margin: 0.15rem 0.2rem;
    padding: 0.3rem;
  }
  & .numerical-guide-header {
    display: flex;
    font-weight: bold;
    margin-bottom: 0.2rem;
  }
  & table {
    display: flex;
    border: none;
    @media print {
      display: table; /* restore table semantics for print */
      width: 100%;
    }
    .wbc-capture & {
      display: table;
      width: 100%;
    }
  }
`;

const TopRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.1in; /* fixed header area */
  flex: 0 0 auto;
  @media print {
    height: 0.75in; /* free a bit more vertical space on page 1 */
  }
`;

const WbcLogo = styled.img`
  position: absolute;
  left: 0.25in;
  top: 50%;
  transform: translateY(-50%);
  height: 0.8in; /* adjust as needed; won't push content */
  width: auto;
  object-fit: contain;
  z-index: 1054; /* under the Header text */
  @media print {
    height: 0.6in; /* smaller in print to fit compressed header */
  }
`;

const FedLogo = styled.img`
  position: absolute;
  right: 0.25in;
  top: 50%;
  transform: translateY(-50%);
  height: 0.8in; /* adjust as needed; won't push content */
  width: auto;
  object-fit: contain;
  z-index: 1054; /* under the Header text */
  @media print {
    height: 0.6in; /* smaller in print to fit compressed header */
  }
`;

const FinalRosterExportDashboard = () => {
  const { isLoading, rostersByFed, tournamentType } = useContext(FinalRosterExportContext);
  const [displayData, setDisplayData] = useState([]);

  const FLAG_URL = window.MLBBest.envVariables.REACT_APP_FLAG_URL;

  const formatBirthDate = useCallback(profile => {
    if (profile.birthDate) {
      return formatDate(profile.birthDate, "DD-MMM-YYYY");
    } else {
      return "-";
    }
  }, []);

  const formatBirthPlace = useCallback(profile => {
    const birthCountry = profile?.birthCountry === "Dominican Republic" ? "Dominican Rep." : profile.birthCountry;
    const birthCity = ["TBD", "N/A"].includes(profile?.birthCity?.toUpperCase())
      ? profile.birthCity.toUpperCase()
      : properNoun(profile?.birthCity);
    if (profile.birthCountry === "United States" && profile.birthCity && profile.birthStateCode) {
      return `${birthCity}, ${profile.birthStateCode.toUpperCase()}`;
    } else if (profile.birthCity && profile.birthCountry) {
      return `${birthCity}, ${birthCountry}`;
    } else if (profile.birthCountry) {
      return profile.birthCountry;
    } else {
      return "-";
    }
  }, []);

  const formatHeight = useCallback(height => {
    if (height > 0) {
      // convert from inches to feet and inches
      const feet = Math.floor(height / 12);
      const inches = height % 12;
      return `${feet}' ${inches}"`;
    }
    return "-";
  }, []);

  const formatProfile = useCallback(
    profile => {
      return {
        profileId: profile?.profileId,
        profileType: profile?.profileType,
        name: `${(profile?.lastName || "").toUpperCase()}, ${properNoun(
          profile?.rosterFirstName || profile?.firstName || ""
        )}`,
        uniformNumber: profile?.uniformNumber || "-",
        positionCategory: profile?.positionCategory || "-",
        position: profile?.position === "Conditioning Coach" ? "Cond. Coach" : profile?.position || "-",
        bats: profile?.bats?.toUpperCase() || "-",
        throws: profile?.throwingArm?.toUpperCase() || "-",
        height: formatHeight(profile?.height),
        weight: profile?.weight || "-",
        birthDate: formatBirthDate(profile),
        birthPlace: formatBirthPlace(profile),
        org: profile?.org && profile.org.toUpperCase() !== "NO" ? profile.org : "-",
        club: profile?.club || "-",
        clubString: profile?.clubString || "-",
        designatedPitcher: profile?.designatedPitcher || false,
        customStaffPosition: profile?.customStaffPosition || null
      };
    },
    [formatBirthDate, formatBirthPlace, formatHeight]
  );

  const renderNumericalGuideRow = useCallback(
    (player, country) => (
      <tr key={`${country}-numerical-guide-row-${player?.profileId}`}>
        <td className="text-wrap">{player.uniformNumber}</td>
        <td className="text-wrap">{`${player.name}${player.designatedPitcher ? "*" : ""}`}</td>
        <td className="text-wrap">{!!player.customStaffPosition ? player.customStaffPosition : player.position}</td>
      </tr>
    ),
    []
  );

  const getOrg = org => {
    return org && org.toLowerCase() === "no" ? "-" : org;
  };

  const getClub = useCallback((org, club, clubString) => {
    if (club == null || club === "") {
      return "-";
    }

    if (getOrg(org) === "-") {
      return clubString;
    }
    return club;
  }, []);

  const renderPlayerRow = useCallback(
    (player, country) => (
      <tr key={`${country}-player-row-${player?.profileId}`}>
        <td className="td-bold">{player.uniformNumber}</td>
        <td className="text-wrap">{`${player.name}${player.designatedPitcher ? "*" : ""}`}</td>
        <td className="text-wrap">{player.position}</td>
        <td className="text-wrap">{player.bats}</td>
        <td className="text-wrap">{player.throws}</td>
        <td className="text-wrap">{player.height}</td>
        <td className="text-wrap">{player.weight}</td>
        <td className="text-wrap">{player.birthDate}</td>
        <td className="text-wrap">{player.birthPlace}</td>
        <td className="text-wrap">{getOrg(player.org)}</td>
        <td className="text-wrap">{getClub(player.org, player.club, player.clubString)}</td>
      </tr>
    ),
    [getClub]
  );

  const renderStaffRow = useCallback(
    (staffMember, country) => (
      <tr key={`${country}-staff-row-${staffMember?.profileId}`}>
        <td className="td-bold">{staffMember.uniformNumber}</td>
        <td className="text-wrap">{staffMember.name}</td>
        <td className="text-wrap">
          {!!staffMember.customStaffPosition ? staffMember.customStaffPosition : staffMember.position}
        </td>
        <td className="text-wrap">{staffMember.birthPlace}</td>
        <td className="text-wrap">{staffMember.org}</td>
        <td className="text-wrap">{staffMember.club}</td>
      </tr>
    ),
    []
  );

  const allDisplayData = useMemo(() => {
    if (rostersByFed && Object.entries(rostersByFed)?.length > 0) {
      return Object.entries(rostersByFed)
        .map(([country, roster]) => {
          const players = roster?.players
            .map(player => formatProfile(player))
            .sort((a, b) => a?.name?.localeCompare(b?.name));
          const numericalGuide = roster?.players
            .concat(
              roster?.staff?.filter(
                staffMember =>
                  staffMember.position !== PositionConstants.STAFF.TEAM_STAFF.name &&
                  staffMember.position !== PositionConstants.STAFF.CUSTOM.value &&
                  !PositionConstants.STAFF.TRAINER.positionIds.includes(staffMember.positionId)
              )
            )
            .map(player => formatProfile(player))
            .sort((a, b) => a?.uniformNumber - b?.uniformNumber);
          const fedDisplayData = {
            country,
            countryCode: roster?.countryCode?.toLowerCase(),
            fedTeamId: players?.[0]?.fedTeamId,
            tournamentType: players?.[0]?.tournamentType === 1 ? QUALIFIERS : MAIN,
            numericalGuide: numericalGuide,
            staff: roster?.staff
              .map(staffMember => formatProfile(staffMember))
              .filter(staffMember => staffMember.position !== PositionConstants.STAFF.CUSTOM.value)
              .sort((a, b) => a?.name?.localeCompare(b?.name)),
            byPosition: {
              pitchers: players.filter(p => p.positionCategory === "Pitcher").sort((a, b) => a?.lastName - b?.lastName),
              catchers: players.filter(p => p.positionCategory === "Catcher").sort((a, b) => a?.lastName - b?.lastName),
              infielders: players
                .filter(p => p.positionCategory === "Infield")
                .sort((a, b) => a?.lastName - b?.lastName),
              outfielders: players
                .filter(p => p.positionCategory === "Outfield")
                .sort((a, b) => a?.lastName - b?.lastName)
            }
          };
          return fedDisplayData;
        })
        .sort((a, b) => a?.country?.localeCompare(b?.country));
    }
    return [];
  }, [rostersByFed, formatProfile]);

  useEffect(() => {
    const elementSelectorsToRemove = ["#best-navbar", "#wbc-footer"];
    elementSelectorsToRemove.forEach(selector => {
      const element = document.querySelector(selector);
      if (element) {
        element.style.display = "none";
      }
    });
    try {
      const ua = window.navigator && window.navigator.userAgent;
      if (ua && /HeadlessChrome|Puppeteer|Playwright/i.test(ua)) {
        document.body.classList.add("wbc-capture");
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    setDisplayData(allDisplayData);
  }, [allDisplayData]);

  const getTeamStaff = useCallback(staff => {
    return staff.filter(
      member => member.position === PositionConstants.STAFF.TEAM_STAFF.name && member.customStaffPosition
    );
  }, []);

  return (
    <>
      {isLoading ? (
        <LoadingPage>
          <div id="loading-wrapper">
            <div className="d-flex mr-2">
              <img src={logo} height="48px" width="48px" alt="wbc-logo" />
            </div>
            Loading...
          </div>
        </LoadingPage>
      ) : (
        <Pages id="dashboard">
          {displayData.map(c => (
            <Page key={c.country}>
              <TopRow>
                <WbcLogo src={tournamentType === QUALIFYING ? logoWithQual : logoWithMain} alt="wbc-logo" />
                <Header className="header">{c.country}</Header>
                <FedLogo src={`${FLAG_URL}/${c?.countryCode}.svg`} alt="fed-logo" />
              </TopRow>
              <TeamStaffBanner>
                {getTeamStaff(c.staff).length > 0 &&
                  getTeamStaff(c.staff).map((member, index) => (
                    <div key={member.profileId} style={{ display: "flex", alignItems: "center", marginRight: "20px" }}>
                      <div className="staff-position">{member.customStaffPosition}:</div>
                      <div className="staff-name" style={{ marginLeft: "5px" }}>
                        {member.name.includes(", ")
                          ? member.name
                              .split(", ")
                              .reverse()
                              .join(" ")
                          : member.name}
                      </div>
                    </div>
                  ))}
              </TeamStaffBanner>
              <Body>
                <LeftContainer>
                  {Object.entries(c.byPosition).map(([position, players]) => (
                    <TableContainer key={`${c.country}-${position})}`}>
                      <table className="table table-responsive table-sm table-borderless">
                        <thead>
                          <tr>
                            <th style={{ width: "0.5in" }}>NO</th>
                            <th style={{ width: "1.5in" }}>
                              {position.toUpperCase()} ({players?.length || 0})
                            </th>
                            <th style={{ width: "0.5in" }}>POS</th>
                            <th style={{ width: "0.25in" }}>B</th>
                            <th style={{ width: "0.25in" }}>T</th>
                            <th style={{ width: "0.45in" }}>HT</th>
                            <th style={{ width: "0.45in" }}>WT</th>
                            <th style={{ width: "0.75in" }}>DOB</th>
                            <th style={{ width: "1.75in" }}>BIRTHPLACE</th>
                            <th style={{ width: "0.65in" }}>ORG</th>
                            <th style={{ width: "1.5in" }}>CLUB</th>
                          </tr>
                        </thead>
                        <tbody>{players.map(player => renderPlayerRow(player, c.country))}</tbody>
                      </table>
                    </TableContainer>
                  ))}
                  <StaffTableContainer>
                    <table className="table table-responsive table-sm table-borderless">
                      <thead>
                        <tr>
                          <th style={{ width: "0.5in" }}>NO</th>
                          <th style={{ width: "1.75in" }}>COACHING STAFF</th>
                          <th style={{ width: "1in" }}>POSITION</th>
                          <th style={{ width: "2in" }}>BIRTHPLACE</th>
                          <th style={{ width: "1in" }}>ORG</th>
                          <th style={{ width: "3in" }}>CLUB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.staff
                          ?.filter(staffMember => staffMember.position !== PositionConstants.STAFF.TEAM_STAFF.name)
                          .map(staffMember => renderStaffRow(staffMember, c.country))}
                      </tbody>
                    </table>
                  </StaffTableContainer>
                </LeftContainer>
                <RightContainer>
                  <NumericalGuide id="numerical-guide">
                    <div className="numerical-guide-header">Numerical Guide</div>
                    <table className="table table-responsive table-sm table-borderless">
                      <tbody>{c.numericalGuide.map(player => renderNumericalGuideRow(player, c.country))}</tbody>
                    </table>
                  </NumericalGuide>
                </RightContainer>
              </Body>
            </Page>
          ))}
        </Pages>
      )}
    </>
  );
};

export default FinalRosterExportDashboard;
